import { ActionIcon, Badge, Button, Flex, Image, Loader, Modal, Stack, Text, TextInput, Tooltip } from "@mantine/core"
import { IconArrowLeft, IconPhoto, IconSearch, IconX } from "@tabler/icons-react"
import { useNavigate, useParams } from "react-router-dom"
import { DataTable } from "mantine-datatable"
import { useDynamicHeight } from "@shared/hooks/responsive.queries.ts"
import { useGetQualityUserRewardsHistory } from "../hooks/quality-queries"
import { useEffect, useRef, useState } from "react"
import { API_URL, PAGE_SIZES } from "@/shared"
import { QualityRewardType } from "../domain/quality-red-flag"
import { headerStore } from "@shared/state/header.state.ts"
import axios from "axios"

function EvidenceButton({ url, index }: { url: string; index: number }) {
  const [opened, setOpened] = useState(false)
  const [src, setSrc] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const objectUrlRef = useRef<string | null>(null)

  const proxyUrl = url.startsWith("graph://") ? `${API_URL}/quality-red-flags/evidence/proxy?ref=${encodeURIComponent(url)}` : url

  const open = async () => {
    setOpened(true)
    if (src) return
    setLoading(true)
    try {
      const resp = await axios.get<Blob>(proxyUrl, { responseType: "blob" })
      const objUrl = URL.createObjectURL(resp.data)
      objectUrlRef.current = objUrl
      setSrc(objUrl)
    } catch {
      setSrc(null)
    } finally {
      setLoading(false)
    }
  }

  const close = () => {
    setOpened(false)
    // Keep objectUrl cached so re-opens are instant; cleaned up on unmount via effect
  }

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [])

  return (
    <>
      <Tooltip label={`Ver adjunto ${index + 1}`}>
        <Button w="fit-content" variant="subtle" size="compact-xs" leftSection={<IconPhoto size={12} />} onClick={open}>
          Adjunto {index + 1}
        </Button>
      </Tooltip>
      <Modal opened={opened} onClose={close} centered size="xl" title={`Evidencia ${index + 1}`}>
        {loading && <Flex justify="center" py="xl"><Loader /></Flex>}
        {!loading && src && <Image src={src} fit="contain" mah="75vh" />}
        {!loading && !src && <Text c="dimmed" ta="center">No se pudo cargar la evidencia.</Text>}
        <Flex justify="flex-end" mt="md">
          <Button component="a" href={proxyUrl} download={`evidencia_${index + 1}`} variant="outline" size="xs">
            Descargar
          </Button>
        </Flex>
      </Modal>
    </>
  )
}

export default function QualityRewardsHistoryPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { tableHeight } = useDynamicHeight()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [searchBy, setSearchBy] = useState("")

  const {
    data: historyData,
    isLoading,
    isFetching,
    refetch
  } = useGetQualityUserRewardsHistory(userId || "", {
    page,
    rows: pageSize,
    issuedBy: searchBy
  })

  useEffect(() => {
    if (!userId) {
      navigate("/quality/red-flags")
    }
  }, [userId, navigate])

  const handleBackClick = () => {
    navigate("/quality/red-flags")
  }

  const userName = historyData?.user?.name || ""
  const pageTitle = userName ? `Historial de Recompensas - ${userName}` : "Historial de Recompensas"

  useEffect(() => {
    headerStore.setTitle(pageTitle)
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [pageTitle, refetch])

  const getRewardTypeLabel = (type: QualityRewardType) => {
    switch (type) {
      case QualityRewardType.BADGE:
        return "Insignia Digital"
      case QualityRewardType.TIME_BONUS:
        return "Bono de Tiempo"
      case QualityRewardType.VACATION_PRIORITY:
        return "Prioridad en Vacaciones"
      default:
        return type
    }
  }

  const getRewardColor = (type: QualityRewardType) => {
    switch (type) {
      case QualityRewardType.BADGE:
        return "blue"
      case QualityRewardType.TIME_BONUS:
        return "green"
      case QualityRewardType.VACATION_PRIORITY:
        return "grape"
      default:
        return "gray"
    }
  }

  return (
    <Flex direction="column" gap="md">
      <Flex justify="flex-end" align="center">
        <Button variant="outline" leftSection={<IconArrowLeft size={16} />} onClick={handleBackClick}>
          Volver
        </Button>
      </Flex>

      <DataTable
        withTableBorder
        borderRadius="md"
        striped
        highlightOnHover
        records={historyData?.data || []}
        totalRecords={historyData?.total || 0}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        onRecordsPerPageChange={setPageSize}
        recordsPerPageOptions={PAGE_SIZES}
        recordsPerPageLabel="Cantidad por página"
        columns={[
          { accessor: "issuedAt", title: "Fecha", render: (record) => (record.issuedAt ? new Date(record.issuedAt).toLocaleDateString() : "-") },
          {
            accessor: "type",
            title: "Recompensa",
            render: (record) => (
              <Badge color={getRewardColor(record.type)} variant="filled">
                {getRewardTypeLabel(record.type)}
              </Badge>
            )
          },
          {
            accessor: "comment",
            title: "Comentario",
            render: (record) => (
              <Text size="sm" lineClamp={2} title={record.comment}>
                {record.comment || "-"}
              </Text>
            )
          },
          {
            accessor: "evidenceUrls",
            title: "Evidencias",
            render: (record) => (
              <Stack gap={4}>
                {record.evidenceUrls && record.evidenceUrls.length > 0 ? (
                  record.evidenceUrls.map((url, index) => <EvidenceButton key={index} url={url} index={index} />)
                ) : (
                  <Text size="xs" c="dimmed">
                    Sin adjuntos
                  </Text>
                )}
              </Stack>
            )
          },
          {
            accessor: "issuedBy",
            title: "Otorgado por",
            filtering: searchBy !== "",
            filter: (
              <TextInput
                leftSection={<IconSearch size={15} stroke={1.5} />}
                rightSection={
                  <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => setSearchBy("")}>
                    <IconX size={14} stroke={1.5} />
                  </ActionIcon>
                }
                placeholder="Emitido por..."
                value={searchBy}
                onChange={(event) => setSearchBy(event.currentTarget.value)}
              />
            )
          }
        ]}
        fetching={isLoading || isFetching}
        noRecordsText="No hay historial de recompensas para este usuario"
        idAccessor="rewardId"
        style={{ height: tableHeight }}
      />
    </Flex>
  )
}
